import { EventRegistrationDealerComponent } from "../components/event-registration/event-registration-dealer/event-registration-dealer.component";
import { EventRegistrationRegComponent } from "../components/event-registration/event-registration-reg-report/event-registration-reg-report.component";
import { EventRegistrationReportComponent } from "../components/event-registration/event-registration-report/event-registration-report.component";
import { ChartComponent } from "../layout/chart/chart.component";
import { DashboardContainerComponent } from "../layout/dashboard-container/dashboard-container.component";
import { FilterListContainerComponent } from "../layout/filter-list-container/filter-list-container.component";
import { SearchConfig, FETCH_METHODS, ApiEnvNames } from "./api.model";
import { CHART_TYPE } from "./chart.model";
import { OptionsFetchConfig } from "./common.model";
import { EventAreaPieChartSchema, EventAreaRegisteredChart, EventAreaTableReport, EventAreaWithdrawnPieChartSchema, EventDealerTableReport, EventDuplicateRegReport, RegisteredVenueChartSchema, EventRegInputHistorySchema } from "./event-registration-schema.model";
import { FieldNames, VenueAreaDropdown, RegistrationStatusDropdown, TotalCountText } from "./form-fields.model";
import { dynamicComponentSchema, SECTION_TYPE, TabConfig } from "./form.model";
export const RegistrationTitle = "Event(s) Registration Dashboard";

export const EventTabIds = {
  Report: 'report',
  Dashboard: 'dashboard',
  Reports: 'reports'
}

export type RegistrationHeaderCard = {
  event_id: string;
  event_name: string;
  status: 'Active' | 'Upcoming' | 'Completed';
  total_venues: number;
  total_registered_count: number;
  total_capacity: number;
  start_date: string;
  mappings?: Record<string, string> | string | null;
}


const SectionNames = {
  EventArea: 'EventArea',
  EventAreaTableReport: 'EventAreaTableReport',
  EventDealerTableReport: 'EventDealerTableReport',
  EventAreaPieSummary: 'EventAreaPieSummary',
  EventAreaWithdrawnPieSummary: 'EventAreaWithdrawnPieSummary',
  EventVenueChart: 'EventVenueChart',
  EventDuplicateRegReport: 'EventDuplicateRegReport',
  EventRegInputHistory: 'EventRegInputHistory'
} as const;

type SECTION_NAMES = typeof SectionNames[keyof typeof SectionNames];

export const  fetchParamsEventListForDashboard: SearchConfig = {
  path: 'getEventListForDashboard',
  method: FETCH_METHODS['Get'],
  urlPath: ApiEnvNames['EventDashboard'],
  body: {},
};

const FetchConfig: Record<SECTION_NAMES, SearchConfig> = {
  [SectionNames['EventArea']]: {
    path: 'eventChartData',
    method: FETCH_METHODS['Get'],
    body: {},
    urlPath: ApiEnvNames['EventDashboard']
  },
  [SectionNames['EventVenueChart']]: {
    path: 'eventDailyRegistrations',
    method: FETCH_METHODS['Post'],
    urlPath: ApiEnvNames['EventDashboard'],
    body: {},
  },
  [SectionNames['EventAreaTableReport']]: {
    path: 'eventVenueSummary',
    method: FETCH_METHODS['Get'],
    urlPath: ApiEnvNames['EventDashboard']
  },
  [SectionNames['EventDealerTableReport']]: {
    path: 'eventDealerSummary',
    method: FETCH_METHODS['Get'],
    urlPath: ApiEnvNames['EventDashboard']
  },
  [SectionNames['EventRegInputHistory']]: {
    path: 'eventRegInputHistory',
    method: FETCH_METHODS['Get'],
    urlPath: ApiEnvNames['EventDashboard'],
  },
  [SectionNames['EventAreaPieSummary']]: {
    path: 'eventAreaSummary',
    method: FETCH_METHODS['Get'],
    urlPath: ApiEnvNames['EventDashboard'],
    body: {}
  },
  [SectionNames['EventAreaWithdrawnPieSummary']]: {
    path: 'eventAreaWithdrawnSummary',
    method: FETCH_METHODS['Get'],
    urlPath: ApiEnvNames['EventDashboard'],
    body: {}
  },
  [SectionNames['EventDuplicateRegReport']]: {
    path: 'getEventAreaDuplicates',
    method: FETCH_METHODS['Get'],
    urlPath: ApiEnvNames['EventDashboard']
  },
}

export const EventDynamicOptionSchema: OptionsFetchConfig = {
  fields: [
    { name: FieldNames['Area'], keyValuePair: { fieldLabel: '', fieldValue: '' } },
    { name: FieldNames['Venue'], keyValuePair: { fieldLabel: '', fieldValue: '', } },
    { name: FieldNames['RegistrationStatus'], keyValuePair: { fieldLabel: '', fieldValue: '', } },
  ],
  searchConfig: {
    method: FETCH_METHODS['Get'],
    path: 'eventChartDropdown',
  }
}

const EventGridSchema: Array<dynamicComponentSchema> = [
  {
    component: ChartComponent,
    type: SECTION_TYPE['Chart'],
    inputs: {
      chartSchema: EventAreaRegisteredChart,
      onLoadFetch: true,
      chartType: CHART_TYPE['ColumnChart'],
      scrollablePlotAreaMinWidth: 4800,
      scrollablePlotAreaRecordThreshold: 14,
      elementId: SectionNames['EventArea'],
      apiConfig: FetchConfig[SectionNames['EventArea']],
      exportConfig: {
        fileName: 'Registration_Count_by_Venue',
        exportMessage: "Registration Count by Venue Data",
        isCamelCase: ['dealerName', 'areaName', 'accountAddress1', 'accountCity'],
        schema: {
          venue: "Venue",
          lexusCentral: "Lexus Central",
          lexusEastern: "Lexus Eastern",
          lexusSouthern: "Lexus Southern",
          lexusWestern: "Lexus Western",
          hq: "HQ"
        }
      },
      filterSchema: {
        FieldsArray: structuredClone([VenueAreaDropdown, RegistrationStatusDropdown]),
      },
      infoFields: structuredClone([{
        ...TotalCountText, calculateBy: ['lexusCentral', 'lexusEastern', 'lexusSouthern', 'lexusWestern', 'hq']
      }]),
    }
  },
  {
    component: ChartComponent,
    type: SECTION_TYPE['Chart'],
    inputs: {
      chartSchema: RegisteredVenueChartSchema['chartSchema'],
      chartType: RegisteredVenueChartSchema['chartType'],
      elementId: SectionNames['EventVenueChart'],
      apiConfig: FetchConfig[SectionNames['EventVenueChart']],
      filterSchema: RegisteredVenueChartSchema['filterSchema'],
      exportConfig: RegisteredVenueChartSchema['exportConfig'],
      infoFields: RegisteredVenueChartSchema['infoFields']
    }
  },
  {
    component: FilterListContainerComponent,
    type: SECTION_TYPE['List'],
    inputs: {
      elementId: SectionNames['EventAreaTableReport'],
      DataTableSchema: EventAreaTableReport,
      forSection: true,
      filterSchema: {
        FieldsArray: structuredClone([]),
      },
      apiConfig: FetchConfig[SectionNames['EventAreaTableReport']],
      onLoadFetch: true,
    }
  },
  {
    component: EventRegistrationDealerComponent,
    type: SECTION_TYPE['List'],
    inputs: {
      elementId: SectionNames['EventDealerTableReport'],
      DataTableSchema: EventDealerTableReport,
      forSection: true,
      filterSchema: {
        FieldsArray: structuredClone([]),
      },
      apiConfig: FetchConfig[SectionNames['EventDealerTableReport']],
      onLoadFetch: true,
      infoFields: structuredClone([{
        ...TotalCountText, calculateBy: 'numberOfRegistration'
      }]),
    }
  },
  {
    component: ChartComponent,
    type: SECTION_TYPE['Chart'],
    inputs: {
      chartSchema: EventAreaPieChartSchema['chartSchema'],
      chartType: EventAreaPieChartSchema['chartType'],
      elementId: SectionNames['EventAreaPieSummary'],
      apiConfig: FetchConfig[SectionNames['EventAreaPieSummary']],
      filterSchema: EventAreaPieChartSchema['filterSchema'],
      exportConfig: EventAreaPieChartSchema['exportConfig'],
      infoFields: EventAreaPieChartSchema['infoFields']
    }
  },
  {
    component: ChartComponent,
    type: SECTION_TYPE['Chart'],
    inputs: {
      chartSchema: EventAreaWithdrawnPieChartSchema['chartSchema'],
      chartType: EventAreaWithdrawnPieChartSchema['chartType'],
      elementId: SectionNames['EventAreaWithdrawnPieSummary'],
      apiConfig: FetchConfig[SectionNames['EventAreaWithdrawnPieSummary']],
      filterSchema: EventAreaWithdrawnPieChartSchema['filterSchema'],
      exportConfig: EventAreaWithdrawnPieChartSchema['exportConfig'],
      infoFields: EventAreaWithdrawnPieChartSchema['infoFields']
    }
  },
  {
    component: FilterListContainerComponent,
    type: SECTION_TYPE['List'],
    inputs: {
      elementId: SectionNames['EventDuplicateRegReport'],
      DataTableSchema: EventDuplicateRegReport,
      forSection: true,
      filterSchema: {
        FieldsArray: structuredClone([]),
      },
      apiConfig: FetchConfig[SectionNames['EventDuplicateRegReport']],
      onLoadFetch: true,
    }
  },
  {
    component: FilterListContainerComponent,
    type: SECTION_TYPE['List'],
    inputs: {
      elementId: SectionNames['EventRegInputHistory'],
      DataTableSchema: EventRegInputHistorySchema,
      forSection: true,
      filterSchema: {
        FieldsArray: structuredClone([]),
      },
      apiConfig: FetchConfig[SectionNames['EventRegInputHistory']],
      onLoadFetch: true,
    }
  }
]

export const EventTabSchema: Array<TabConfig> = [
  {
    label: 'Enrollment Overview',
    tabId: EventTabIds['Report'],
    type: SECTION_TYPE.FilterWithTable,
    component: EventRegistrationRegComponent,
    inputs: {
    }
  },
  {
    label: 'Metrics',
    tabId: EventTabIds['Dashboard'],
    type: SECTION_TYPE.Dashboard,
    component: DashboardContainerComponent,
    inputs: {
      gridSchema: EventGridSchema
    }
  },
  {
    label: 'Reports',
    tabId: EventTabIds['Reports'],
    type: SECTION_TYPE.FilterWithTable,
    component: EventRegistrationReportComponent,
    inputs: {
    }
  }

];